import { useState, useEffect, useRef } from 'react';
import Constants from '../assests/Constants/constants';
import ReportsService from '../Services/reports.service';
import { 
  generateTransactionMapping, 
  filterTransactionsByZeroBalance, 
  calculateTotalIncome,
  generateUniqueCategories
} from '../Utils/ReportUtils';

export const useProfitAndLossData = (data, showAccountCardsWithZeros, showPeriodComparison, periodComparisonData) => {
  // State for profit and loss names
  const [profitAndLossNames, setProfitAndLossNames] = useState([]);
  
  // State for filtered transactions
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [comparisonTransactions, setComparisonTransactions] = useState([]);
  
  // State for total income
  const [totalIncome, setTotalIncome] = useState(1);
  const [comparisonTotalIncome, setComparisonTotalIncome] = useState(0);
  
  // State for unique categories
  const [uniqueCategories, setUniqueCategories] = useState({ uniquePalCodeCategories: [], uniquePalCodes: [] });
  
  // Trigger to force the generation of the report
  const [showReport, setShowReport] = useState(false);

  /**
   * Identify which "palCodeAccountGroup" belongs to P&L.
   * The profit & loss section contains only תוצאתי (result account) palcodes,
   * so a group is part of P&L when it has at least one תוצאתי palcode.
   * This considers both periods to ensure all P&L groups are included.
   */
  const rebuildProfitAndLossNames = (data1, data2) => {
    const newProfitAndLossNames = [];

    // Helper function to process transactions and extract P&L groups.
    // The profit & loss section contains only תוצאתי (result account) palcodes.
    const processPeriodTransactions = (transactions) => {
      transactions.forEach(item => {
        const groupName = item.palCodeAccountGroup;
        const palCodeType = item.palCodeType;

        if (palCodeType === Constants.ResultAccountName) {
          if (!newProfitAndLossNames.includes(groupName)) {
            newProfitAndLossNames.push(groupName);
          }
        }
      });
    };

    // Process both periods
    if (data1 && data1.transaction) {
      processPeriodTransactions(data1.transaction);
    }
    if (data2 && data2.transaction) {
      processPeriodTransactions(data2.transaction);
    }

    setProfitAndLossNames(newProfitAndLossNames);
    setShowReport(true);
  };

  /**
   * Process and filter transaction data
   */
  const processTransactionData = () => {
    if (!data || !data.transaction?.length) return;

    // Determine which data to use for the first period
    let firstPeriodData, secondPeriodData;
    
    if (showPeriodComparison && periodComparisonData.data1 && periodComparisonData.data2) {
      // In comparison mode, use the selected periods
      firstPeriodData = periodComparisonData.data1;
      secondPeriodData = periodComparisonData.data2;
    } else {
      // In normal mode, use the current data for first period
      firstPeriodData = data;
      secondPeriodData = null;
    }

    // Get all P&L transactions from first period
    let firstPeriodTransactions = firstPeriodData.transaction.filter(tr =>
      profitAndLossNames.includes(tr.palCodeAccountGroup)
    );

    // Get second period data if in comparison mode
    let secondPeriodTransactions = null;
    if (secondPeriodData) {
      secondPeriodTransactions = secondPeriodData.transaction.filter(tr =>
        profitAndLossNames.includes(tr.palCodeAccountGroup)
      );
    }

    // Create comprehensive transaction mapping
    const transactionMapping = generateTransactionMapping(firstPeriodTransactions, secondPeriodTransactions);

    // Filter transactions based on zero-balance setting
    const { filteredTransactions: newFilteredTransactions, comparisonTransactions: newComparisonTransactions } = 
      filterTransactionsByZeroBalance(transactionMapping, showAccountCardsWithZeros, secondPeriodData);

    // Set filtered transactions
    setFilteredTransactions(newFilteredTransactions);
    setComparisonTransactions(newComparisonTransactions);

    // Calculate total income
    const calculatedTotalIncome = calculateTotalIncome(newFilteredTransactions);
    setTotalIncome(calculatedTotalIncome);

    // Calculate comparison total income if it exists
    if (newComparisonTransactions.length > 0) {
      const calculatedComparisonTotalIncome = calculateTotalIncome(newComparisonTransactions);
      setComparisonTotalIncome(calculatedComparisonTotalIncome);
    } else {
      setComparisonTotalIncome(0);
    }

    // Generate unique categories
    const categories = generateUniqueCategories(newFilteredTransactions);
    setUniqueCategories(categories);
  };

  // Rebuild profit and loss names when data changes
  useEffect(() => {
    if (data && data.transaction?.length > 0) {
      const comparisonData = (showPeriodComparison && periodComparisonData.data2) ? periodComparisonData.data2 : null;
      rebuildProfitAndLossNames(data, comparisonData);
    }
  }, [data]);

  // Process transaction data when dependencies change
  useEffect(() => {
    processTransactionData();
  }, [showAccountCardsWithZeros, showPeriodComparison, periodComparisonData, profitAndLossNames, showReport]);

  return {
    profitAndLossNames,
    filteredTransactions,
    comparisonTransactions,
    totalIncome,
    comparisonTotalIncome,
    uniqueCategories,
    showReport,
    setShowReport,
    rebuildProfitAndLossNames
  };
};
